import { FC, useCallback, useEffect, useMemo, useRef } from 'react';

import { useRecoilValue } from 'recoil';

import { postImage } from '@shared/api/uploadImage';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { mainColorGenerator } from '@shared/utils/mainColorGenerator';
import { DeltaStatic, Sources } from 'quill/index';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface IProps {
  value: string;
  onChange: (
    value: string,
    delta: DeltaStatic,
    source: Sources,
    editor: ReactQuill.UnprivilegedEditor,
  ) => void;
  assetRole?: LowerRole;
}

const TextEditor: FC<IProps> = ({ value, onChange, assetRole }: IProps) => {
  const customTheme = useRecoilValue(customThemeAtom);
  const quillRef = useRef<ReactQuill>(null);
  const { bugoRole } = useAuth();

  // assetRole이 없으면 useAuth에서 가져온 bugoRole 사용
  const uploadRole = assetRole ?? bugoRole() ?? LowerRole.Public;

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      try {
        const image = await postImage(file, uploadRole);
        const editor = quillRef.current?.getEditor();

        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', fileBucketUrl(image.url, 'raw'));
          editor.setSelection({ index: range.index + 1, length: 0 });
        }
      } catch (error) {
        console.error('이미지 업로드 실패:', error);
        alert('이미지 업로드에 실패했습니다.');
      }
    };
  }, [uploadRole]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ color: [] }, { background: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
    }),
    [imageHandler],
  );

  useEffect(() => {
    const mainColor = mainColorGenerator(customTheme);

    // https://stackoverflow.com/questions/37801882/how-to-change-css-root-color-variables-in-javascript
    document.documentElement.style.setProperty('--main-color', mainColor);
  }, [customTheme]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={modules}
      placeholder="내용을 입력해주세요."
      className="theme-text-1 theme-bg-1"
    />
  );
};

export default TextEditor;
